/**
 * **Whether one session has cost its client a credit** (booking-system R10) — the mobile app's
 * `features/booking/model/creditConsumption.ts`, ported 1:1.
 *
 * The balance and the history are the SERVER's and are never recomputed here. What this answers is a
 * question about ONE session in hand — may the trainer cancel its charge, and what does the panel
 * say it did to the client's credits — which the calendar read does not carry. The rule therefore
 * lives on both sides of the wire, and one fixture pins it: `__fixtures__/credit-consumption-parity
 * .json` is the backend's own (`train-with-me-test/Fixtures/credit-consumption-parity.json`, the
 * same file `Modules/Booking/Domain/CreditConsumption.cs` is pinned by), replayed in
 * `creditConsumption.test.ts`. A failing vector is a real divergence, never a test to relax.
 *
 * An unknown ORIGIN charges nothing (the backend's own arm: over-charging is worse than waiting for
 * the fixture); an unknown STATUS follows its origin's rule.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

/** The three columns R10 keys on, as the calendar publishes them. */
export interface CreditConsumptionInput {
  /** The wire's `origin`, or null on a row that predates R19's column. */
  readonly origin: string | null | undefined;
  /** The wire's raw `status`. */
  readonly status: string | null | undefined;
  /** Whether a trainer is on the other side of the session (`trainerId != null`). */
  readonly carriesTrainer: boolean;
}

/** Consumed at CREATION — the credit doubles as the reservation guarantee. `null` reads as trainer-made. */
export const CONSUMED_AT_CREATION: ReadonlySet<string> = new Set(['SelfBooked', 'Trainer']);

/** Consumed at an OUTCOME — a twelve-week plan would otherwise cost thirty-six credits the day it lands. */
export const CONSUMED_AT_OUTCOME: ReadonlySet<string> = new Set(['Plan', 'RecurringSeries']);

/** The one status that gives a creation-consumed credit back: an early cancel. */
const REFUNDING_STATUS = 'Cancelled';

/** The outcomes somebody can point at — the only ones a plan or series session pays for. */
const CHARGING_OUTCOMES: ReadonlySet<string> = new Set(['Completed', 'LateCancelled', 'NoShow']);

export function consumesCredit({
  origin,
  status,
  carriesTrainer,
}: CreditConsumptionInput): boolean {
  if (!carriesTrainer) return false;

  if (origin == null || CONSUMED_AT_CREATION.has(origin)) return status !== REFUNDING_STATUS;
  if (CONSUMED_AT_OUTCOME.has(origin)) return status != null && CHARGING_OUTCOMES.has(status);

  // `BookingLink` — paid on site, outside the ledger (R24) — and every origin added after this build.
  return false;
}
